import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import { ArrowRight, Copy, Check, ChevronDown, X, Sparkles } from 'lucide-react';

const C = {
  bg: '#131413',
  accent: '#9EAFAC',
  accentDark: '#586060',
  border: 'rgba(158,175,172,0.15)',
  card: 'rgba(158,175,172,0.04)',
};

const MOBILE_BREAKPOINT = 768;

function NodeCard({ top, left, right, bottom, label, value, delay = '0s', reverse }) {
  return (
    <div
      style={{
        position: 'absolute',
        top,
        left,
        right,
        bottom,
        background: 'rgba(19,20,19,0.75)',
        border: `1px solid ${C.border}`,
        borderRadius: '16px',
        padding: '14px 18px',
        backdropFilter: 'blur(16px)',
        animation: `${reverse ? 'float-reverse' : 'float'} 4s ease-in-out infinite`,
        animationDelay: delay,
        zIndex: 5,
        minWidth: '140px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: C.accent,
            animation: 'pulse-dot 2s infinite',
            animationDelay: delay,
          }}
        />
        <span style={{ color: C.accent, fontSize: '12px', fontFamily: 'Outfit, sans-serif' }}>
          {label}
        </span>
      </div>
      <div
        style={{
          color: 'white',
          fontFamily: 'Outfit, sans-serif',
          fontWeight: 700,
          fontSize: '18px',
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= MOBILE_BREAKPOINT;
  });

  const referralLink = `${window.location.origin}/ref/${user?.referral_code}`;
  const isActive = user?.investment_status === 'active';

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const media = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handleChange = (event) => setIsMobile(event.matches);

    setIsMobile(media.matches);

    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }

    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (!isActive) return;
    const key = `vg_welcome_shown_${user?.id}`;
    if (!localStorage.getItem(key)) {
      setShowWelcome(true);
      localStorage.setItem(key, '1');
    }
  }, [isActive, user?.id]);

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const earningsRows = [
    ['2 invitados', 'Inicio de red', '14.3%'],
    ['7 posiciones', 'Retiro disponible', '50%'],
    ['14 posiciones', 'Ciclo completo', '100%'],
  ];

  return (
    <div
      style={{
        background: C.bg,
        minHeight: '100vh',
        color: 'white',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <Navbar />

      {showWelcome && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div
            style={{
              background: '#0d0e0d',
              border: '1px solid rgba(158,175,172,0.2)',
              borderRadius: '24px',
              padding: '36px',
              maxWidth: '480px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 0 60px rgba(158,175,172,0.1)',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setShowWelcome(false)}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                background: 'none',
                border: 'none',
                color: '#586060',
                cursor: 'pointer',
              }}
            >
              <X size={18} />
            </button>

            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(158,175,172,0.1)',
                border: '1px solid rgba(158,175,172,0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                boxShadow: '0 0 24px rgba(158,175,172,0.15)',
                animation: 'pulse-glow 2s infinite',
              }}
            >
              <Sparkles size={28} color="#9EAFAC" />
            </div>

            <h2
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontSize: '26px',
                fontWeight: 800,
                color: 'white',
                marginBottom: '10px',
                letterSpacing: '-0.5px',
              }}
            >
              ¡Comenzó tu viaje! 🚀
            </h2>

            <p style={{ color: '#9EAFAC', fontSize: '16px', marginBottom: '8px' }}>
              Tu inversión fue confirmada.
            </p>

            <p
              style={{
                color: '#586060',
                fontSize: '14px',
                marginBottom: '28px',
                lineHeight: 1.6,
              }}
            >
              Ahora puedes ganar hasta{' '}
              <span style={{ color: '#4ade80', fontWeight: 700 }}>$600</span> por completar tu ciclo.
              <br />
              Comparte tu enlace y empieza hoy.
            </p>

            <div
              style={{
                background: 'rgba(158,175,172,0.05)',
                border: '1px solid rgba(158,175,172,0.15)',
                borderRadius: '12px',
                padding: '14px',
                marginBottom: '16px',
              }}
            >
              <div
                style={{
                  color: '#586060',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: '8px',
                }}
              >
                Tu enlace de referido
              </div>
              <code style={{ color: '#9EAFAC', fontSize: '13px', wordBreak: 'break-all' }}>
                {referralLink}
              </code>
            </div>

            <button
              onClick={copyLink}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '14px',
                background: copied ? 'rgba(74,222,128,0.1)' : 'rgba(158,175,172,0.1)',
                border: `1px solid ${
                  copied ? 'rgba(74,222,128,0.3)' : 'rgba(158,175,172,0.25)'
                }`,
                borderRadius: '12px',
                color: copied ? '#4ade80' : '#9EAFAC',
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
                marginBottom: '12px',
                fontFamily: 'Inter, sans-serif',
                transition: 'all 0.2s',
              }}
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
              {copied ? '¡Copiado!' : 'Copiar enlace de referido'}
            </button>

            <button
              onClick={() => setShowWelcome(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#586060',
                fontSize: '13px',
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      <section
        style={{
          position: 'relative',
          minHeight: isMobile ? 'auto' : '100vh',
          height: isMobile ? 'auto' : '100vh',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '110px 0 72px' : '0',
        }}
      >
        {!isMobile && (
          <>
            <div
              style={{
                position: 'absolute',
                top: '-10%',
                right: '-5%',
                width: '65%',
                height: '110%',
                background:
                  'radial-gradient(ellipse at 55% 40%, rgba(210,220,215,0.18) 0%, rgba(158,175,172,0.10) 30%, rgba(88,96,96,0.05) 55%, transparent 75%)',
                filter: 'blur(50px)',
                pointerEvents: 'none',
                animation: 'pulse-glow 6s ease-in-out infinite',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: '10%',
                left: '5%',
                width: '30%',
                height: '40%',
                background: 'radial-gradient(ellipse, rgba(88,96,96,0.06) 0%, transparent 70%)',
                filter: 'blur(40px)',
                pointerEvents: 'none',
              }}
            />

            <svg
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                opacity: 0.06,
                pointerEvents: 'none',
              }}
            >
              <defs>
                <pattern id="grid" width="80" height="80" patternUnits="userSpaceOnUse">
                  <path d="M 80 0 L 0 0 0 80" fill="none" stroke="#9EAFAC" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>

            <svg
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 4,
              }}
            >
              <line
                x1="16%"
                y1="28%"
                x2="38%"
                y2="48%"
                stroke="rgba(158,175,172,0.15)"
                strokeWidth="1"
                strokeDasharray="4 6"
              />
              <line
                x1="84%"
                y1="28%"
                x2="62%"
                y2="48%"
                stroke="rgba(158,175,172,0.15)"
                strokeWidth="1"
                strokeDasharray="4 6"
              />
              <line
                x1="12%"
                y1="72%"
                x2="38%"
                y2="55%"
                stroke="rgba(158,175,172,0.12)"
                strokeWidth="1"
                strokeDasharray="4 6"
              />
              <line
                x1="86%"
                y1="70%"
                x2="62%"
                y2="55%"
                stroke="rgba(158,175,172,0.12)"
                strokeWidth="1"
                strokeDasharray="4 6"
              />
            </svg>

            <NodeCard top="22%" left="6%" label="Primer nivel" value="2 invitaciones" delay="0s" />
            <NodeCard
              top="18%"
              right="7%"
              label="Red Global"
              value="Creciendo"
              delay="0.8s"
              reverse
            />
            <NodeCard bottom="22%" left="4%" label="Tu Red" value="14 posiciones" delay="1.6s" />
            <NodeCard
              bottom="20%"
              right="6%"
              label="Tu Meta"
              value="$600"
              delay="2.4s"
              reverse
            />
          </>
        )}

        <div
          style={{
            position: 'relative',
            zIndex: 10,
            textAlign: 'center',
            maxWidth: '780px',
            padding: isMobile ? '0 20px' : '0 24px',
            animation: isMobile ? 'none' : 'fade-up 0.8s ease both',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '32px',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(158,175,172,0.08)',
                border: `1px solid ${C.border}`,
                borderRadius: '50px',
                padding: '6px 16px',
              }}
            >
              <div
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: C.accent,
                  animation: isMobile ? 'none' : 'pulse-dot 1.5s infinite',
                }}
              />
              <span style={{ color: C.accent, fontSize: '13px', fontWeight: 500 }}>
                Red Activa · Únete Ahora
              </span>
              <ArrowRight size={13} color={C.accent} />
            </div>
          </div>

          <h1
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 900,
              lineHeight: 1.05,
              fontSize: isMobile ? 'clamp(44px, 14vw, 64px)' : 'clamp(52px, 8vw, 96px)',
              letterSpacing: '-2px',
              marginBottom: '24px',
            }}
          >
            <span style={{ color: 'white' }}>Activa tu red con </span>
            <span style={{ color: C.accent }}>2 invitaciones</span>
          </h1>

          <p
            style={{
              color: C.accent,
              fontSize: 'clamp(16px, 2vw, 20px)',
              maxWidth: '560px',
              margin: '0 auto 40px',
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            Tu ciclo comienza con $100 y 2 invitaciones. A partir de ahí, la red crece por niveles hasta completar el sistema. Al cerrar el ciclo, puedes ganar hasta <strong style={{ color: 'white' }}>$600.</strong>
          </p>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              onClick={() => navigate('/invest')}
              data-testid="hero-invest-button"
              className="btn-glow"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '14px 28px',
                borderRadius: '50px',
                background: C.accent,
                border: 'none',
                color: '#131413',
                fontWeight: 700,
                fontSize: '16px',
                boxShadow: `0 0 30px rgba(158,175,172,0.3)`,
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                if (isMobile) return;
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.boxShadow = '0 0 50px rgba(255,255,255,0.3)';
              }}
              onMouseLeave={(e) => {
                if (isMobile) return;
                e.currentTarget.style.background = C.accent;
                e.currentTarget.style.boxShadow = '0 0 30px rgba(158,175,172,0.3)';
              }}
            >
              Activar mi ciclo <ArrowRight size={18} />
            </button>

            <button
              onClick={() => document.getElementById('how')?.scrollIntoView({ behavior: 'smooth' })}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 28px',
                borderRadius: '50px',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'white',
                fontWeight: 600,
                fontSize: '16px',
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                if (isMobile) return;
                e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
              }}
              onMouseLeave={(e) => {
                if (isMobile) return;
                e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
              }}
            >
              ¿Cómo funciona?
            </button>
          </div>

          {user?.investment_status === 'active' && (
            <div
              style={{
                marginTop: '36px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: 'rgba(158,175,172,0.08)',
                border: '1px solid rgba(158,175,172,0.22)',
                borderRadius: '18px',
                padding: '12px 18px',
                maxWidth: '720px',
                marginLeft: 'auto',
                marginRight: 'auto',
                flexWrap: 'wrap',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 20,
              }}
            >
              <span style={{ color: 'rgba(255,255,255,0.78)', fontSize: '13px', fontWeight: 700 }}>Tu enlace:</span>
              <code
                style={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '13px',
                  wordBreak: 'break-all',
                  overflowWrap: 'anywhere',
                  flex: '1 1 240px',
                  textAlign: 'left',
                }}
              >
                {referralLink}
              </code>
              <button
                onClick={copyLink}
                style={{
                  background: 'none',
                  border: 'none',
                  color: C.accent,
                  cursor: 'pointer',
                  display: 'flex',
                }}
                data-testid="copy-referral-link"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
              <div style={{ flexBasis: '100%', color: C.accentDark, fontSize: '12px', lineHeight: 1.45 }}>
                Comparte siempre tu link personal. Tus 2 invitados deben registrarse desde este enlace.
              </div>
            </div>
          )}
        </div>

        {!isMobile && (
          <div
            style={{
              position: 'absolute',
              bottom: '32px',
              left: '32px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              color: C.accentDark,
              fontSize: '13px',
              zIndex: 10,
            }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: `1px solid ${C.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'float 3s ease-in-out infinite',
              }}
            >
              <ChevronDown size={16} />
            </div>
            <span>01 / 05 · Scroll</span>
          </div>
        )}
      </section>

      <section id="how" style={{ padding: '120px 24px', maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '72px', animation: 'fade-up 0.6s ease both' }}>
          <p
            style={{
              color: C.accentDark,
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '12px',
            }}
          >
            02 / 05
          </p>
          <h2
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(36px, 5vw, 60px)',
              color: 'white',
              marginBottom: '16px',
            }}
          >
            Tan Simple <br />
            <span style={{ color: C.accent }}>De Ejecutar</span>
          </h2>
          <p
            style={{
              color: C.accentDark,
              fontSize: '18px',
              maxWidth: '500px',
              margin: '0 auto',
              lineHeight: 1.6,
            }}
          >
            Tu red avanza cuando cada persona cumple su parte.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
          }}
        >
          {[
            {
              n: '01',
              emoji: '1',
              title: 'Entras con $100',
              desc: 'Activas tu cuenta y abres tu ciclo dentro de la red.',
              highlight: 'Activación inicial',
            },
            {
              n: '02',
              emoji: '2',
              title: 'Invitas a 2 personas',
              desc: 'Tu primer objetivo es traer 2 personas directas para iniciar el crecimiento de tu red.',
              highlight: 'Tu primer nivel',
            },
            {
              n: '03',
              emoji: '3',
              title: 'Ellos también invitan a 2',
              desc: 'Cada persona que entra debe invitar a otras 2 para que la red siga avanzando por niveles.',
              highlight: 'Crecimiento duplicado',
            },
            {
              n: '04',
              emoji: '4',
              title: 'La red solo avanza si todos participan',
              desc: 'Si alguien no invita a sus 2 personas, esa parte de la red se detiene y el ciclo tarda más en completarse.',
              highlight: 'Trabajo en equipo',
            },
            {
              n: '05',
              emoji: '5',
              title: 'Completas el ciclo y ganas',
              desc: 'Cuando la red completa sus posiciones, el ciclo se cierra y puedes ganar hasta $600.',
              highlight: 'Ciclo completo',
            },
          ].map((s, i) => (
            <div
              key={i}
              className="card-hover"
              style={{
                background: 'rgba(19,20,19,0.8)',
                border: `1px solid ${C.border}`,
                borderRadius: '24px',
                padding: '36px 28px',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-10px',
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 900,
                  fontSize: '100px',
                  color: 'rgba(158,175,172,0.05)',
                  lineHeight: 1,
                  userSelect: 'none',
                }}
              >
                {s.n}
              </div>
              <div style={{ fontSize: '40px', marginBottom: '20px' }}>{s.emoji}</div>
              <h3
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 800,
                  fontSize: '26px',
                  color: 'white',
                  marginBottom: '12px',
                }}
              >
                {s.title}
              </h3>
              <p style={{ color: C.accentDark, lineHeight: 1.7, marginBottom: '20px' }}>{s.desc}</p>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'rgba(158,175,172,0.08)',
                  border: `1px solid ${C.border}`,
                  borderRadius: '50px',
                  padding: '6px 14px',
                }}
              >
                <div
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: C.accent,
                  }}
                />
                <span style={{ color: C.accent, fontSize: '13px', fontWeight: 600 }}>
                  {s.highlight}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section style={{ padding: '0 24px 120px', maxWidth: '1240px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '48px', animation: 'fade-up 0.6s ease both' }}>
          <p
            style={{
              color: C.accentDark,
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '12px',
            }}
          >
            03 / 05
          </p>
          <h2
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(34px, 5vw, 58px)',
              color: 'white',
              marginBottom: '16px',
            }}
          >
            Así Crece <span style={{ color: C.accent }}>Tu Red</span>
          </h2>
          <p
            style={{
              color: C.accentDark,
              fontSize: '18px',
              maxWidth: '680px',
              margin: '0 auto',
              lineHeight: 1.65,
            }}
          >
            Cada persona invita a 2 más. La red avanza por niveles hasta completar las 14 posiciones de tu ciclo.
          </p>
        </div>

        <div
          className="card-hover"
          style={{
            background: 'rgba(19,20,19,0.9)',
            border: `1px solid ${C.border}`,
            borderRadius: '28px',
            padding: isMobile ? '22px' : '28px',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '0.62fr 1.38fr',
            gap: isMobile ? '28px' : '26px',
            alignItems: 'center',
            boxShadow: '0 24px 70px rgba(0,0,0,0.28)',
            overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', textAlign: isMobile ? 'center' : 'left' }}>
            <div>
              <p
                style={{
                  color: C.accent,
                  fontFamily: 'Outfit, sans-serif',
                  fontSize: isMobile ? '22px' : '20px',
                  fontWeight: 800,
                  lineHeight: 1.25,
                  marginBottom: '12px',
                }}
              >
                Al llegar a 7 posiciones, ganas $300. Al completar 14 posiciones, ganas $600.
              </p>
              <p style={{ color: C.accentDark, lineHeight: 1.7, fontSize: '15px', margin: 0 }}>
                Tu primer objetivo es activar tu cuenta, invitar a 2 personas con tu link personal y mantener el crecimiento de la red por niveles.
              </p>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: '10px',
              }}
            >
              {[
                ['Nivel 1', '2 personas'],
                ['Nivel 2', '4 personas'],
                ['Nivel 3', '8 personas'],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    border: `1px solid ${C.border}`,
                    background: 'rgba(158,175,172,0.05)',
                    borderRadius: '16px',
                    padding: '12px',
                  }}
                >
                  <div style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
                    {label}
                  </div>
                  <div style={{ color: 'white', fontFamily: 'Outfit, sans-serif', fontSize: '18px', fontWeight: 800 }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
              }}
            >
              {[
                ['7 posiciones', '$300'],
                ['14 posiciones', '$600'],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    border: '1px solid rgba(158,175,172,0.24)',
                    background: 'rgba(158,175,172,0.08)',
                    borderRadius: '18px',
                    padding: '16px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ color: C.accentDark, fontSize: '12px', marginBottom: '6px' }}>{label}</div>
                  <div style={{ color: C.accent, fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 900 }}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          <div
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${C.border}`,
              borderRadius: '24px',
              padding: isMobile ? '12px' : '10px',
              boxShadow: 'inset 0 0 40px rgba(158,175,172,0.04)',
              minHeight: isMobile ? 'auto' : '430px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <img
              src="https://pub-a598f3b72aa449a9bd7c17e51278022e.r2.dev/ChatGPT%20Image%2030%20abr%202026%2C%2010_11_10%20a.m..png"
              alt="Visual de red por niveles VaultGrow"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: '18px',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      </section>

      <section style={{ padding: '0 24px 120px', maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '56px' }}>
          <p
            style={{
              color: C.accentDark,
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              marginBottom: '12px',
            }}
          >
            04 / 05
          </p>
          <h2
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(32px, 5vw, 56px)',
              color: 'white',
              marginBottom: '16px',
            }}
          >
            El Ciclo Que Puedes
            <br />
            <span style={{ color: C.accent }}>Completar</span>
          </h2>
        </div>

        <div
          style={{
            background: 'rgba(19,20,19,0.9)',
            border: `1px solid ${C.border}`,
            borderRadius: '24px',
            overflow: 'hidden',
          }}
        >
          {!isMobile && (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  padding: '16px 24px',
                  borderBottom: `1px solid ${C.border}`,
                  background: 'rgba(158,175,172,0.04)',
                }}
              >
                {['Posiciones pagadas', 'Puedes ganar', 'Avance'].map((h, i) => (
                  <div
                    key={i}
                    style={{
                      color: C.accentDark,
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    {h}
                  </div>
                ))}
              </div>

              {earningsRows.map(([label, amount, progress], i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    padding: '18px 24px',
                    borderBottom: i < earningsRows.length - 1 ? `1px solid rgba(158,175,172,0.06)` : 'none',
                    background: label === '14 posiciones' ? 'rgba(158,175,172,0.06)' : 'transparent',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (label !== '14 posiciones') e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                  }}
                  onMouseLeave={(e) => {
                    if (label !== '14 posiciones') e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <div style={{ color: 'white', fontWeight: 600 }}>
                    {label}
                  </div>
                  <div style={{ color: C.accent }}>{amount}</div>
                  <div
                    style={{
                      color: String(progress).includes('-') ? '#9ca3af' : '#34d399',
                      fontWeight: 600,
                    }}
                  >
                    {progress}
                  </div>
                </div>
              ))}
            </>
          )}

          {isMobile && (
            <div style={{ padding: '14px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {earningsRows.map(([label, amount, progress], i) => (
                  <div
                    key={i}
                    style={{
                      border: `1px solid ${label === '14 posiciones' ? 'rgba(158,175,172,0.3)' : 'rgba(158,175,172,0.12)'}`,
                      background: label === '14 posiciones' ? 'rgba(158,175,172,0.08)' : 'rgba(158,175,172,0.03)',
                      borderRadius: '14px',
                      padding: '12px',
                      display: 'grid',
                      gap: '8px',
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'center' }}>
                      <span style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Posiciones pagadas</span>
                      <span style={{ color: 'white', fontWeight: 700, textAlign: 'right' }}>
                        {label}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'center' }}>
                      <span style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Puedes ganar</span>
                      <span style={{ color: C.accent, fontWeight: 600, textAlign: 'right' }}>{amount}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'center' }}>
                      <span style={{ color: C.accentDark, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Avance</span>
                      <span style={{ color: String(progress).includes('-') ? '#9ca3af' : '#34d399', fontWeight: 700, textAlign: 'right' }}>{progress}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <p style={{ color: C.accentDark, textAlign: 'center', marginTop: '16px', fontSize: '13px' }}>
          * El avance muestra el porcentaje de posiciones completadas dentro del ciclo.
        </p>
      </section>

      <section style={{ padding: '80px 24px 120px', textAlign: 'center' }}>
        <p
          style={{
            color: C.accentDark,
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            marginBottom: '12px',
          }}
        >
          05 / 05
        </p>
        <h2
          style={{
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(40px, 6vw, 72px)',
            color: 'white',
            marginBottom: '24px',
            letterSpacing: '-1.5px',
          }}
        >
          ¿Estás Dentro?
        </h2>
        <p
          style={{
            color: C.accentDark,
            fontSize: '20px',
            maxWidth: '460px',
            margin: '0 auto 40px',
          }}
        >
          Cada pago confirmado puede acercar tu ciclo a $600.
        </p>
        <button
          onClick={() => navigate('/invest')}
          data-testid="bottom-invest-button"
          className="btn-glow"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            padding: '18px 40px',
            borderRadius: '50px',
            background: C.accent,
            border: 'none',
            color: '#131413',
            fontFamily: 'Outfit, sans-serif',
            fontWeight: 800,
            fontSize: '20px',
            boxShadow: `0 0 40px rgba(158,175,172,0.3)`,
            transition: 'all 0.3s',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            if (isMobile) return;
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 0 60px rgba(158,175,172,0.5)';
          }}
          onMouseLeave={(e) => {
            if (isMobile) return;
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 0 40px rgba(158,175,172,0.3)';
          }}
        >
          Empezar Ahora <ArrowRight size={22} />
        </button>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '32px',
            marginTop: '48px',
            flexWrap: 'wrap',
          }}
        >
          {[
            ['$100', 'Inversión única'],
            ['Viernes', 'Día de retiro'],
            ['A Tu Banco', 'Pago seguro'],
          ].map(([v, l]) => (
            <div key={l} style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 800,
                  fontSize: '22px',
                  color: C.accent,
                }}
              >
                {v}
              </div>
              <div style={{ color: C.accentDark, fontSize: '13px', marginTop: '2px' }}>{l}</div>
            </div>
          ))}
        </div>
      </section>

      <footer
        style={{
          borderTop: `1px solid ${C.border}`,
          padding: '24px',
          textAlign: 'center',
          color: C.accentDark,
          fontSize: '13px',
        }}
      >
        © 2026 VaultGrow · Todos los derechos reservados
      </footer>
    </div>
  );
}
